import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getPost, likePost, deletePost } from "@/lib/api";
import { PostCard } from "@/pages/DashboardPage";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PostDetailPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const userStr = localStorage.getItem("user") || sessionStorage.getItem("user");
    if (userStr) {
      try {
        setCurrentUserId(JSON.parse(userStr).user_id);
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (!postId) return;
    setLoading(true);
    getPost(postId)
      .then((res) => {
        setPost(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? "Post not found" : "Failed to load post");
        setLoading(false);
      });
  }, [postId]);

  const handleLike = async (id) => {
    try {
      const res = await likePost(id);
      setPost((prev) => ({
        ...prev,
        user_liked: res.data.liked,
        like_count: res.data.like_count,
        likes: res.data.liked
          ? [...(prev.likes || []), currentUserId]
          : (prev.likes || []).filter((l) => l !== currentUserId),
      }));
    } catch {
      toast.error("Failed to like post");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePost(id);
      toast.success("Post deleted");
      navigate("/dashboard", { replace: true });
    } catch {
      toast.error("Failed to delete post");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="font-['Crimson_Text'] text-xl text-[#2D2A26] mb-4">{error || "Post not found"}</p>
        <Button onClick={() => navigate("/dashboard")} variant="outline" className="border-[#E5E0D5] font-['Nunito']">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Feed
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1.5 text-sm text-[#6B665E] hover:text-[#2D2A26] font-['Nunito'] mb-4 transition-colors"
        data-testid="back-to-feed"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <PostCard
        post={post}
        onLike={handleLike}
        onComment={() => {}}
        currentUserId={currentUserId}
        onDelete={handleDelete}
        defaultShowComments
      />
    </div>
  );
}
